import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import enhancedAuthService from '../services/enhanced-auth-service';
import EnhancedSessionManager from '../utils/EnhancedSessionManager';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  sessionId: string | null;
  login: (googleToken: string) => Promise<string>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  getFreshToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sessionId, setSessionId] = useState<string | null>(null);

  /**
   * Fetch fresh user data from /auth/me endpoint
   * This ensures we have the latest user data including profile picture URL
   */
  const fetchFreshUserData = async (): Promise<User | null> => {
    try {
      const token = await EnhancedSessionManager.getAccessToken();
      if (!token) {
        console.log('ℹ️ AuthContext: No token available for fetching fresh user data');
        return null;
      }

      console.log('🔄 AuthContext: Fetching fresh user data from /auth/me...');
      const response = await fetch('https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/auth/me', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn('⚠️ AuthContext: Failed to fetch fresh user data:', response.status);
        return null;
      }

      const data = await response.json();
      if (data.success && data.user) {
        console.log('✅ AuthContext: Fresh user data loaded from backend', {
          userId: data.user.id,
          hasTryOnPicture: !!data.user.tryOnProfilePictureUrl,
          tryOnPictureUrl: data.user.tryOnProfilePictureUrl
        });

        return {
          id: data.user.id,
          email: data.user.email,
          displayName: data.user.displayName,
          profilePicture: data.user.profilePicture || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
          createdAt: new Date(data.user.createdAt),
          lastLogin: new Date(data.user.lastLogin),
          likedOutfits: data.user.likedOutfits || [],
          uploadedOutfits: data.user.uploadedOutfits || [],
          tryOnProfilePictureUrl: data.user.tryOnProfilePictureUrl,
          isVerified: data.user.isVerified || false,
          isPrivate: data.user.isPrivate || false,
          username: data.user.username,
          followerCount: data.user.followerCount || 0,
          followingCount: data.user.followingCount || 0,
          emailVerified: data.user.emailVerified || false,
          tryOnPictureUploadedAt: data.user.tryOnPictureUploadedAt ? new Date(data.user.tryOnPictureUploadedAt) : undefined,
          gender: data.user.gender,
          savedOutfits: data.user.savedOutfits || [],
        };
      }

      return null;
    } catch (error) {
      console.error('❌ AuthContext: Error fetching fresh user data:', error);
      return null;
    }
  };

  // Initialize authentication on mount
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🔍 AuthContext: Initializing authentication...');
      
      try {
        // ENHANCED FIX: Check session directly first
        const sessionInfo = EnhancedSessionManager.getSessionInfo();
        console.log('🔍 AuthContext: Current session check:', sessionInfo ? 'Found' : 'None');
        
        if (sessionInfo && sessionInfo.userId) {
          console.log('✅ AuthContext: Found valid session directly');
          
          // Check if token is not expired
          const isExpired = sessionInfo.needsRefresh;
          console.log('🔍 AuthContext: Token expired?', isExpired);
          
          if (!isExpired) {
            // Get the actual user data from the session manager
            const currentUser = EnhancedSessionManager.getUser();
            if (currentUser) {
              const userData: User = {
                id: currentUser.id,
                email: currentUser.email,
                displayName: currentUser.displayName,
                profilePicture: currentUser.profilePicture || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
                createdAt: currentUser.createdAt ? new Date(currentUser.createdAt) : new Date(),
                lastLogin: currentUser.lastLogin ? new Date(currentUser.lastLogin) : new Date(),
                likedOutfits: currentUser.likedOutfits || [],
                uploadedOutfits: currentUser.uploadedOutfits || [],
                tryOnProfilePictureUrl: currentUser.tryOnProfilePictureUrl,
                isVerified: currentUser.isVerified || false,
                isPrivate: currentUser.isPrivate || false,
                username: currentUser.username,
                followerCount: currentUser.followersCount || 0,
                followingCount: currentUser.followingCount || 0,
                emailVerified: currentUser.emailVerified || false,
                tryOnPictureUploadedAt: currentUser.tryOnPictureUploadedAt ? new Date(currentUser.tryOnPictureUploadedAt) : undefined,
                savedOutfits: currentUser.savedOutfits || [],
              };
              
              setUser(userData);
              setIsAuthenticated(true);
              
              console.log('✅ AuthContext: Session restored successfully');
              console.log('👤 AuthContext: User:', userData.displayName);
              console.log('🖼️ AuthContext: Try-On Picture from session:', userData.tryOnProfilePictureUrl || 'NOT SET');
              
              // Fetch fresh user data from backend to ensure we have latest profile picture
              // This is important because the session storage may not have the latest data
              console.log('🔄 AuthContext: Fetching fresh user data to sync profile picture...');
              const freshUser = await fetchFreshUserData();
              if (freshUser) {
                console.log('✅ AuthContext: Fresh user data loaded, updating state');
                console.log('🖼️ AuthContext: Try-On Picture from backend:', freshUser.tryOnProfilePictureUrl || 'NOT SET');
                
                setUser(freshUser);
                
                // Update session storage with fresh data
                EnhancedSessionManager.updateUser(freshUser as any);
                console.log('✅ AuthContext: Session storage updated with fresh user data');
              } else {
                console.log('⚠️ AuthContext: Could not fetch fresh user data, using session data');
              }
              
              // Try to get session info
              try {
                const sessionInfo = EnhancedSessionManager.getSessionInfo();
                if (sessionInfo?.sessionId) {
                  setSessionId(sessionInfo.sessionId);
                }
              } catch (sessionInfoError) {
                console.warn('⚠️ AuthContext: Could not get session info:', sessionInfoError);
              }
              
              setIsLoading(false);
              return;
            }
          } else {
            console.log('⏰ AuthContext: Token expired, attempting refresh...');
            
            // Try to refresh the token
            try {
              const isLoggedIn = await EnhancedSessionManager.isLoggedIn();
              if (isLoggedIn) {
                console.log('✅ AuthContext: Token refreshed successfully');
                // Retry initialization
                setTimeout(() => initializeAuth(), 100);
                return;
              }
            } catch (refreshError) {
              console.warn('⚠️ AuthContext: Token refresh failed:', refreshError);
            }
          }
        }
        
        // FALLBACK: Try the original restoreSession method
        console.log('🔄 AuthContext: Trying restoreSession fallback...');
        const session = await EnhancedSessionManager.restoreSession();
        if (session && session.token && session.user) {
          console.log('✅ AuthContext: Found existing session via restoreSession');
          
          const userData: User = {
            id: session.user.id,
            email: session.user.email,
            displayName: session.user.displayName,
            profilePicture: session.user.profilePicture || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
            createdAt: session.user.createdAt ? new Date(session.user.createdAt) : new Date(),
            lastLogin: session.user.lastLogin ? new Date(session.user.lastLogin) : new Date(),
            likedOutfits: session.user.likedOutfits || [],
            uploadedOutfits: session.user.uploadedOutfits || [],
            tryOnProfilePictureUrl: session.user.tryOnProfilePictureUrl,
            isVerified: session.user.isVerified || false,
            isPrivate: session.user.isPrivate || false,
            username: session.user.username,
            followerCount: session.user.followersCount || 0,
            followingCount: session.user.followingCount || 0,
            emailVerified: session.user.emailVerified || false,
            tryOnPictureUploadedAt: session.user.tryOnPictureUploadedAt ? new Date(session.user.tryOnPictureUploadedAt) : undefined,
            savedOutfits: session.user.savedOutfits || [],
          };
          
          setUser(userData);
          setIsAuthenticated(true);
          
          console.log('✅ AuthContext: Session restored via fallback');
          console.log('👤 AuthContext: User:', userData.displayName);
          console.log('🖼️ AuthContext: Try-On Picture from session:', userData.tryOnProfilePictureUrl || 'NOT SET');
          
          // Fetch fresh user data from backend to ensure we have latest profile picture
          console.log('🔄 AuthContext: Fetching fresh user data to sync profile picture...');
          const freshUser = await fetchFreshUserData();
          if (freshUser) {
            console.log('✅ AuthContext: Fresh user data loaded, updating state');
            console.log('🖼️ AuthContext: Try-On Picture from backend:', freshUser.tryOnProfilePictureUrl || 'NOT SET');
            
            setUser(freshUser);
            
            // Update session storage with fresh data
            EnhancedSessionManager.updateUser(freshUser as any);
            console.log('✅ AuthContext: Session storage updated with fresh user data');
          } else {
            console.log('⚠️ AuthContext: Could not fetch fresh user data, using session data');
          }
          
          // Set session ID if available
          try {
            const sessionInfo = EnhancedSessionManager.getSessionInfo();
            if (sessionInfo?.sessionId) {
              setSessionId(sessionInfo.sessionId);
            }
          } catch (sessionInfoError) {
            console.warn('⚠️ AuthContext: Could not get session info:', sessionInfoError);
          }
        } else {
          console.log('ℹ️ AuthContext: No existing session found');
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ AuthContext: Initialization error:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    // Handle auth state refresh events
    const handleAuthRefresh = async () => {
      console.log('🔄 AuthContext: Received auth:refresh-state event!');
      console.log('🔄 AuthContext: Refreshing auth state...');
      setIsLoading(true);
      await initializeAuth();
    };

    // Handle enhanced auth change events
    const handleEnhancedAuthChange = async () => {
      console.log('🔄 AuthContext: Received enhanced-auth-change event!');
      setIsLoading(true);
      await initializeAuth();
    };

    // Initial auth check
    initializeAuth();

    // Listen for auth refresh events
    window.addEventListener('auth:refresh-state', handleAuthRefresh);
    window.addEventListener('enhanced-auth-change', handleEnhancedAuthChange);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('auth:refresh-state', handleAuthRefresh);
      window.removeEventListener('enhanced-auth-change', handleEnhancedAuthChange);
    };
  }, []); // Empty dependency array - runs only once

  const login = async (googleToken: string): Promise<string> => {
    console.log('🔐 AuthContext: Starting login...');
    setIsLoading(true);
    
    try {
      const authResult = await enhancedAuthService.loginWithGoogle(googleToken, {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        screen: `${window.screen.width}x${window.screen.height}`,
        platform: navigator.platform
      });

      console.log('✅ AuthContext: Login successful');

      const user: User = {
        id: authResult.user.id,
        email: authResult.user.email,
        displayName: authResult.user.displayName,
        profilePicture: authResult.user.profilePicture || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
        createdAt: authResult.user.createdAt ? new Date(authResult.user.createdAt) : new Date(),
        lastLogin: new Date(),
        likedOutfits: authResult.user.likedOutfits || [],
        uploadedOutfits: authResult.user.uploadedOutfits || [],
        tryOnProfilePictureUrl: authResult.user.tryOnProfilePictureUrl,
        isVerified: authResult.user.isVerified || false,
        isPrivate: authResult.user.isPrivate || false,
        username: authResult.user.username,
        followerCount: authResult.user.followersCount || 0,
        followingCount: authResult.user.followingCount || 0,
        emailVerified: authResult.user.emailVerified || false,
        tryOnPictureUploadedAt: authResult.user.tryOnPictureUploadedAt ? new Date(authResult.user.tryOnPictureUploadedAt) : undefined,
        savedOutfits: authResult.user.savedOutfits || [],
      };

      setUser(user);
      setIsAuthenticated(true);
      setSessionId(authResult.session?.sessionId || null);
      setIsLoading(false);

      return authResult.session?.sessionId || '';
    } catch (error) {
      console.error('❌ AuthContext: Login failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      setSessionId(null);
      setIsLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    console.log('🚪 AuthContext: Starting logout...');
    setIsLoading(true);
    
    try {
      await EnhancedSessionManager.logout();
      setUser(null);
      setIsAuthenticated(false);
      setSessionId(null);
      console.log('✅ AuthContext: Logout successful');
    } catch (error) {
      console.error('❌ AuthContext: Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      
      // Persist to session storage via EnhancedSessionManager
      try {
        EnhancedSessionManager.updateUser(userData as any);
        console.log('✅ AuthContext: User updated and persisted to session');
      } catch (error) {
        console.error('❌ AuthContext: Failed to persist user update:', error);
      }
    }
  };

  const getFreshToken = async (): Promise<string> => {
    try {
      const token = await EnhancedSessionManager.getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      return token;
    } catch (error) {
      console.error('❌ AuthContext: Error getting fresh token:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    sessionId,
    login,
    logout,
    updateUser,
    getFreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
